import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { supabase } from '../auth/supabase-client';
import { useReadyAuth } from '../auth/use-auth';
import { SweepControl } from '../features/sweep-control/components/SweepControl';
import { CandidateReview } from '../features/candidate-review/components/CandidateReview';
import { CuratorQueue } from '../features/curator-queue/components/CuratorQueue';
import { SignalAdministration } from '../features/curator-queue/components/SignalAdministration';
import { settingsSectionVisibility } from '../features/curator-queue/settings-sections';
import { MagicLinks } from './MagicLinks';
import { useAssignableUsers } from '../features/signals/hooks';
import { useCreateMagicLink } from '../features/magic-links/hooks';
import { ConsultantRosterImport } from '../features/consultant-roster/components/ConsultantRosterImport';
import { ConsultantAccessAdmin } from '../features/consultant-access/components/ConsultantAccessAdmin';
import { RadarAdmin } from '../features/radar/RadarAdmin';

/**
 * Settings page.
 *
 * v0.7's Settings was where users entered third-party API keys (Claude,
 * Tavily, Apify, SAM.gov, Seamless) that the browser then used to call those
 * services directly. That pattern is intentionally gone: all third-party calls
 * run server side inside Edge Functions and no privileged key is ever sent to
 * the browser.
 *
 * Role-gated operational controls live here rather than in primary navigation.
 * The surface intentionally omits superseded preview routes and hardcoded
 * source counts; live source truth belongs to Sweep health, and signal
 * assignment remains on the signal itself.
 */

type ReportType = 'pursuit_brief' | 'activity';

// On-demand report downloads, relocated from the retired Pursuits page. Each
// invokes generate_report with its report_type at firm_rollup scope.
const reportDownloads: { type: ReportType; label: string }[] = [
  { type: 'pursuit_brief', label: 'Pursuit Brief' },
  { type: 'activity', label: 'Activity Report' },
];

function CreateMagicLinkPanel({
  signalId,
  company,
  headline,
  onDismiss,
}: {
  signalId: string;
  company: string;
  headline: string;
  onDismiss: () => void;
}) {
  const { data: recipients = [], isPending: loadingUsers } = useAssignableUsers();
  const [recipientId, setRecipientId] = useState('');
  const [copied, setCopied] = useState(false);
  const create = useCreateMagicLink();

  useEffect(() => {
    if (!recipientId && recipients.length > 0) setRecipientId(recipients[0]!.id);
  }, [recipients, recipientId]);

  const url = create.data?.url;

  return (
    <div className="bdse-glass-surface p-4 mb-4">
      <div className="bdse-nav-section" style={{ paddingLeft: 0 }}>
        Create a Magic Link
      </div>
      <p className="bdse-page-subtitle mb-3">
        {company}
        {headline ? ` · ${headline}` : ''}
      </p>
      {!url ? (
        <>
          <select
            value={recipientId}
            disabled={loadingUsers || recipients.length === 0}
            onChange={(e) => setRecipientId(e.target.value)}
          >
            {recipients.length === 0 ? (
              <option value="">
                {loadingUsers ? 'Loading teammates…' : 'No teammates available'}
              </option>
            ) : (
              recipients.map((recipient) => (
                <option key={recipient.id} value={recipient.id}>
                  {recipient.name}
                </option>
              ))
            )}
          </select>
          <p className="bdse-page-subtitle" style={{ margin: '4px 0' }}>
            The recipient opens a token view at /s/… — no login required.
          </p>
          <button
            type="button"
            disabled={create.isPending || !recipientId}
            onClick={() => recipientId && create.mutate({ signalId, recipientId })}
            className="bdse-nav-item"
            style={{
              marginLeft: 0,
              marginRight: 0,
              cursor: create.isPending ? 'not-allowed' : 'pointer',
            }}
          >
            {create.isPending ? 'Creating…' : 'Create a Magic Link'}
          </button>
        </>
      ) : (
        <>
          <input readOnly value={url} style={{ width: '100%' }} />
          <button
            type="button"
            onClick={() => {
              void navigator.clipboard?.writeText(url);
              setCopied(true);
            }}
            className="bdse-nav-item"
            style={{ marginLeft: 0, marginRight: 0, marginTop: 6 }}
          >
            {copied ? 'Link copied' : 'Copy link'}
          </button>
        </>
      )}
      <button
        type="button"
        onClick={onDismiss}
        className="bdse-page-subtitle"
        style={{
          display: 'block',
          marginTop: 6,
          background: 'none',
          border: 'none',
          cursor: 'pointer',
        }}
      >
        Dismiss
      </button>
    </div>
  );
}

export function Settings() {
  const { profile } = useReadyAuth();
  const isAdmin = profile.role === 'admin' || profile.role === 'super_admin';
  const [searchParams, setSearchParams] = useSearchParams();
  const shareSignalId = searchParams.get('shareSignal');
  const shareCompany = searchParams.get('company') ?? '';
  const shareHeadline = searchParams.get('headline') ?? '';
  const [sharingOpen, setSharingOpen] = useState(Boolean(shareSignalId));
  useEffect(() => {
    if (shareSignalId) setSharingOpen(true);
  }, [shareSignalId]);
  const dismissShare = () => {
    const next = new URLSearchParams(searchParams);
    next.delete('shareSignal');
    next.delete('company');
    next.delete('headline');
    setSearchParams(next, { replace: true });
  };
  // Firm-wide report access is server-derived independently of role. The Edge
  // Function remains the authorization boundary; this only presents the control.
  const canDownloadReport = profile.role === 'super_admin' || profile.report_access;
  // Signal curation is a third, independent capability - never conferred by role
  // rank, and modelled in settings-sections.ts so all four visibility cases can
  // be proven without rendering this page.
  const sections = settingsSectionVisibility(profile);
  const [generating, setGenerating] = useState<ReportType | null>(null);
  const [failed, setFailed] = useState<ReportType | null>(null);

  async function downloadReport(reportType: ReportType) {
    setGenerating(reportType);
    setFailed(null);
    const now = new Date();
    const end = now.toISOString().slice(0, 10);
    const start = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), 1))
      .toISOString()
      .slice(0, 10);
    const { data, error } = await supabase.functions.invoke('generate_report', {
      body: { report_type: reportType, period: { start, end }, scope: 'firm_rollup' },
    });
    if (error || typeof data !== 'string') {
      setGenerating(null);
      setFailed(reportType);
      return;
    }
    const slug = reportType === 'pursuit_brief' ? 'pursuit-brief' : 'activity-report';
    const url = URL.createObjectURL(new Blob([data], { type: 'text/html;charset=utf-8' }));
    const link = document.createElement('a');
    link.href = url;
    link.download = `${slug}-${start}-${end}.html`;
    link.click();
    URL.revokeObjectURL(url);
    setGenerating(null);
  }

  return (
    <div>
      <header className="bdse-page-header">
        <h1 className="bdse-page-title">Settings</h1>
        <p className="bdse-page-subtitle">Account and platform preferences</p>
      </header>

      {/*
        Signal curation is its OWN landmark, deliberately outside the Admin /
        Reports wrapper below. can_curate_signals is not a rank above admin and
        not a subset of it - it defaults false for every user including
        super_admin, and role never confers it - so a curator who is not an admin
        must see this without being shown, or filed under, Admin.
      */}
      {sections.curation && (
        <section aria-label="Signal curation" className="mt-8 max-w-6xl">
          <CuratorQueue />
          <SignalAdministration />
        </section>
      )}

      {isAdmin && (
        <section aria-label="Magic Links" className="mt-8 max-w-6xl">
          <details
            className="settings-disclosure"
            open={sharingOpen}
            onToggle={(event) => setSharingOpen(event.currentTarget.open)}
          >
            <summary className="settings-disclosure__summary">
              <span>
                <strong>Signal sharing</strong>
                <small>Create, monitor, and revoke secure Magic Links.</small>
              </span>
              <span className="settings-disclosure__chevron" aria-hidden="true">
                ⌄
              </span>
            </summary>
            <div className="settings-disclosure__body">
              {shareSignalId && (
                <CreateMagicLinkPanel
                  signalId={shareSignalId}
                  company={shareCompany}
                  headline={shareHeadline}
                  onDismiss={dismissShare}
                />
              )}
              <MagicLinks />
            </div>
          </details>
        </section>
      )}

      {(isAdmin || canDownloadReport) && (
        <section aria-label="Admin" className="mt-8 max-w-6xl">
          {isAdmin && (
            <>
              <SweepControl />

              <RadarAdmin />

              <CandidateReview />

              <div
                className="bdse-nav-section bdse-nav-section-spaced"
                style={{ paddingLeft: 0 }}
              >
                Data administration
              </div>
              <ConsultantAccessAdmin canManageRoles={profile.role === 'super_admin'} />
              {profile.role === 'super_admin' && <ConsultantRosterImport />}
            </>
          )}

          <div className="bdse-nav-section bdse-nav-section-spaced" style={{ paddingLeft: 0 }}>
            Reports
          </div>
          {reportDownloads.map((r) => {
            const busy = generating === r.type;
            const disabled = !canDownloadReport || generating !== null;
            return (
              <button
                key={r.type}
                type="button"
                disabled={disabled}
                title={
                  canDownloadReport
                    ? `Download the firm-wide ${r.label} as HTML`
                    : 'Practice reports activate with practice attribution'
                }
                onClick={() => downloadReport(r.type)}
                className="bdse-nav-item"
                style={{
                  marginLeft: 0,
                  marginRight: 0,
                  width: '100%',
                  fontFamily: 'inherit',
                  cursor: disabled ? 'not-allowed' : 'pointer',
                  opacity: disabled ? 0.6 : 1,
                }}
              >
                <span className="bdse-nav-item-label">
                  <span className="bdse-nav-dot" style={{ background: 'var(--bdse-teal)' }} />
                  {busy ? 'Generating...' : r.label}
                </span>
              </button>
            );
          })}
          <p className="bdse-page-subtitle" style={{ margin: '6px 0 0' }}>
            Firm-wide Pursuit Brief and Activity Report, generated on demand.
          </p>
          {isAdmin && !canDownloadReport && (
            <p className="bdse-page-subtitle" style={{ margin: '4px 0 0' }}>
              Practice reports activate with practice attribution.
            </p>
          )}
          {failed && (
            <span
              role="status"
              className="bdse-page-subtitle"
              style={{ margin: '4px 0 0', display: 'block' }}
            >
              {failed === 'pursuit_brief' ? 'Pursuit Brief' : 'Activity Report'} generation failed.
              Please try again.
            </span>
          )}
        </section>
      )}
    </div>
  );
}
